import React, { useEffect, useMemo, useState } from 'react'
import { cn } from '@utils/safeTs'
import styles from './MyAccountDownloadModal.module.sass'
import useTrans from '@hooks/useTrans'
import { Button, FormCheckbox, Modal } from '@components/atoms'
import { api } from '@utils/index'
import { MYACCOUNTORDINI, MYACCOUNTPERSONALIZZAZIONIORDINE } from '@utils/endpoint'
import { OrdineListType, PersonalizzazioneOrdineType } from '@utils/types'
import LoadingSvg from '@components/atoms/Loading/Loading'

export type MyAccountDownloadModalItem = {
  id: string
  label: string
  disabled?: boolean
}

export interface MyAccountDownloadModalProps {
  open: boolean
  type?: 'ordine' | 'personalizzazione'
  ordine?: OrdineListType
  personalizzazione?: PersonalizzazioneOrdineType
  id?: string
  title?: string
  subtitle?: string
  description?: string
  onClose?: () => void
  className?: string
}

const MyAccountDownloadModal = ({
  open,
  type = 'ordine',
  ordine,
  personalizzazione,
  id = 'myaccount-download-modal',
  title = 'Download',
  subtitle,
  description = 'Seleziona i file che desideri scaricare:',
  onClose,
  className,
}: MyAccountDownloadModalProps) => {

  const t = useTrans()
  const items = [
    { key: 'immagini', label: t("Immagini prodotto") },
    { key: 'loghi', label: t("Loghi") },
    { key: 'bozze', label: t("Bozze digitali") },
  ]
  const [loading, setLoading] = useState(false)
  const [availableItems, setAvailableItems] = useState([])
  const [selection, setSelection] = useState<string[]>([])

  const downloadUrl = useMemo(() => selection?.length > 0 
    ? type == "personalizzazione" && personalizzazione 
      ? `${api.defaults.baseURL}${MYACCOUNTPERSONALIZZAZIONIORDINE}${personalizzazione?.id}/download_files/?types=${selection.join(",")}`
      : type == "ordine" && ordine 
        ? `${api.defaults.baseURL}${MYACCOUNTORDINI}${ordine?.numeroProtocollo}/download_files/?types=${selection.join(",")}`
        : undefined
    : undefined, [selection, type, ordine, personalizzazione])

  const handleToggle = (itemId: string) => {
    setSelection(prev => prev.includes(itemId)
      ? prev.filter((value) => value !== itemId)
      : [...prev, itemId])
  }

  useEffect(() => {
    setAvailableItems([])
    setSelection([])
    setLoading(true)
    const url = type == 'personalizzazione' && personalizzazione 
      ? `${MYACCOUNTPERSONALIZZAZIONIORDINE}${personalizzazione.id}/downloads/` 
      : type == 'ordine' && ordine 
      ? `${MYACCOUNTORDINI}${ordine.numeroProtocollo}/downloads/`
      : undefined

    if (url)
      api
        .get(url)
        .then((response) => {
          setAvailableItems(response.data)
        })
        .catch(() => setAvailableItems([]))
        .finally(() => setLoading(false))

  }, [ordine, personalizzazione])

  return (
    <Modal
      id={id}
      title={title}
      subtitle={subtitle}
      className={cn(styles.root, loading ? styles['root--loading'] : '', className)}
      open={open}
      headerClassName={styles.header}
      bodyClassName={styles.body}
      onClose={onClose}
      buttons={(
        <>
          {onClose ? (
            <Button
              label={t("Chiudi")}
              variant='secondary'
              onClick={onClose}
            />
          ) : null}
          <Button
            label={t("Download")}
            variant="primary"
            href={downloadUrl}
            disabled={!downloadUrl}
          />
        </>
      )}
    >
      
      {items?.length ? (
        <>
          {loading && <div className={styles.loading}><LoadingSvg /></div>}
          {!!description && (
            <p className={styles.description}>
              {t("Seleziona i file che desideri scaricare:")}
            </p>
          )}
          <ul className={styles.list}>
            {items.map((item) => {
              const inputId = `${id}-${item.key}`
              return (
                <li key={item.key} className={styles.listItem}>
                  <FormCheckbox 
                    id={inputId}
                    size="sm"
                    label={item.label}
                    checked={availableItems.includes(item.key) && selection.includes(item.key)}
                    disabled={!availableItems.includes(item.key)}
                    onChange={() => handleToggle(item.key)}
                  />
                </li>
              )
            })}
          </ul>
        </>
      ) : (
        <p className={styles.emptyMessage}>{t("Nessun file disponibile")}</p>
      )}
    </Modal>
  )
}

export default MyAccountDownloadModal
